import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dna, ChevronDown, AlertCircle } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import MethodologyInfo from "@/components/MethodologyInfo";

/**
 * GeneticNutritionSection — iter705 Phase 1.
 *
 * Показва личните ДНК варианти на потребителя + действени нутригеномни
 * препоръки за всеки variant. Източник: GET /api/breast-intelligence/genetic-profile
 * (чете от lab_entries panel='dna').
 *
 * Образователно — НЕ е диагностично. Винаги директива към консултация
 * с лекар при високи дози добавки или high-penetrance варианти.
 */
const TX = {
  bg: {
    title:    "Генетичен профил → нутригеномни препоръки",
    subtitle: "Лични модулации на хранителните Ви навици според Вашите ДНК варианти.",
    empty:    "Все още не сте качили ДНК резултати.",
    emptyHelp:"Качете PDF или CSV от 23andMe, MyHeritage, AncestryDNA или клиничен генетичен тест в секция Анализи.",
    levers:   "Действия",
    levelsCard: "Препоръки",
    notRec:   "Генотипът Ви не съвпада със зрелите rules. Обновете данните в правилен формат (напр. C/T, Val/Met, null/present).",
  },
  en: { title: "Genetic profile → nutrigenomic actions",
        subtitle: "Personalised nutrition modulation based on your DNA variants.",
        empty: "No DNA results uploaded yet.", emptyHelp: "Upload a 23andMe/MyHeritage/Ancestry PDF/CSV in the Analyses section.",
        levers: "Actions", notRec: "Genotype format not recognised yet.",
        levelsCard: "Recommendations" },
};

const EVIDENCE_COLORS = {
  A: { light: "bg-emerald-100 text-emerald-700", dark: "bg-emerald-500/20 text-emerald-300" },
  B: { light: "bg-blue-100    text-blue-700",    dark: "bg-blue-500/20    text-blue-300" },
  C: { light: "bg-gray-100    text-gray-700",    dark: "bg-gray-500/20    text-gray-300" },
};

export default function GeneticNutritionSection({ lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${API}/breast-intelligence/genetic-profile`);
        if (alive) setData(data);
      } catch (_) {
        /* ignore */
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchProfile();
    const onRefresh = () => fetchProfile();
    window.addEventListener("bhi:dna:refresh", onRefresh);
    return () => { alive = false; window.removeEventListener("bhi:dna:refresh", onRefresh); };
  }, [API]);

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]"
    : "bg-white border-gray-200";

  if (loading) {
    return (
      <div className={`${cardCls} rounded-2xl border p-5 text-center opacity-60`}>
        Зареждане…
      </div>
    );
  }

  const variants = data?.variants || [];
  const totalLevers = data?.total_levers || 0;
  const supportedTotal = data?.supported_variants_total || 0;

  return (
    <section className="space-y-3" data-testid="genetic-nutrition-section">
      <div className="flex items-center gap-2 flex-wrap">
        <Dna className="w-5 h-5 text-violet-500" />
        <h2 className="text-lg sm:text-xl font-bold">{t.title}</h2>
        {data?.methodology && (
          <MethodologyInfo
            methodology={data.methodology}
            limitations={data.limitations}
            lang={lang}
            isDark={isDark}
            testid="genetic-nutrition"
          />
        )}
      </div>
      <p className="text-xs opacity-60">{t.subtitle}</p>

      {variants.length === 0 ? (
        <div className={`${cardCls} rounded-2xl border p-6`} data-testid="genetic-empty">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">{t.empty}</div>
              <p className="text-sm opacity-70">{t.emptyHelp}</p>
              <p className="text-xs opacity-50 mt-2">
                Поддържани варианти: {supportedTotal} (MTHFR, COMT, GSTM1/T1, CYP1B1, VDR, BCMO1, BRCA1, и т.н.)
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={`${cardCls} rounded-2xl border p-4 flex items-center justify-between`}>
            <div className="text-sm">
              <span className="font-bold text-violet-600 dark:text-violet-300">{variants.length}</span>
              <span className="opacity-60"> вариант{variants.length === 1 ? "" : "а"} · </span>
              <span className="font-bold text-violet-600 dark:text-violet-300">{totalLevers}</span>
              <span className="opacity-60"> препоръки</span>
            </div>
            <div className="text-xs opacity-50">от {supportedTotal} поддържани</div>
          </div>

          {variants.map((v, i) => {
            const open = openIdx === i;
            return (
              <div
                key={v.marker_code}
                className={`${cardCls} rounded-2xl border overflow-hidden`}
                data-testid={`variant-card-${v.marker_code}`}
              >
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className={`w-full flex items-center justify-between p-4 transition ${
                    isDark ? "hover:bg-white/5" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{v.name_bg}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"
                      }`}>
                        {v.genotype_norm || "—"}
                      </span>
                      {!v.recognised && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] ${
                          isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700"
                        }`}>
                          ⚠ format
                        </span>
                      )}
                    </div>
                    <div className="text-xs opacity-60 mt-0.5">{v.function_bg}</div>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <div className={`px-4 pb-4 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                    {v.phenotype_bg && (
                      <div className={`mt-3 p-3 rounded-lg text-sm ${
                        isDark ? "bg-violet-500/10" : "bg-violet-50"
                      }`}>
                        <span className="font-semibold">Фенотип: </span>
                        {v.phenotype_bg}
                      </div>
                    )}

                    {!v.recognised && (
                      <div className={`mt-3 p-3 rounded-lg text-xs ${
                        isDark ? "bg-amber-500/10 text-amber-200" : "bg-amber-50 text-amber-800"
                      }`}>
                        {t.notRec}
                      </div>
                    )}

                    {v.levers && v.levers.length > 0 && (
                      <div className="mt-4">
                        <div className="text-[11px] font-bold uppercase tracking-wider opacity-60 mb-2">
                          {t.levelsCard}
                        </div>
                        <ul className="space-y-2.5">
                          {v.levers.map((l, j) => {
                            const ec = EVIDENCE_COLORS[l.evidence] || EVIDENCE_COLORS.C;
                            return (
                              <li
                                key={j}
                                className={`p-3 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}
                              >
                                <div className="flex items-start gap-2">
                                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold shrink-0 ${
                                    isDark ? ec.dark : ec.light
                                  }`}>
                                    {l.evidence}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold leading-snug">
                                      {l.action_bg}
                                    </div>
                                    <div className="text-xs opacity-70 mt-1 leading-relaxed">
                                      {l.rationale_bg}
                                    </div>
                                    {l.cite && (
                                      <div className="text-[10px] opacity-50 mt-1 italic">
                                        📚 {l.cite}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}
